import { useEffect, useState, useCallback } from "react";
import api, { apiErr, API } from "@/lib/api";
import LiveMap from "@/components/LiveMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Shield, Tv, Radio, Users, DollarSign, Wallet, Flag, Settings as SettingsIcon, Mail,
  Loader2, BadgeCheck, Trash2, Plus, ScrollText, CheckCircle2, XCircle, Clock, Smartphone,
  Monitor, Tablet, Globe, MapPin, Timer, BarChart3, FolderOpen, Signal, ExternalLink, FileText, Award, CreditCard, AlertTriangle,
} from "lucide-react";

const STATUS_COLORS = {
  draft: "bg-slate-500/15 text-slate-500", pending: "bg-amber-500/15 text-amber-600",
  approved: "bg-blue-500/15 text-blue-600", verified: "bg-emerald-500/15 text-emerald-600",
  suspended: "bg-rdcred/15 text-rdcred", rejected: "bg-rdcred/15 text-rdcred",
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${color}`}><Icon size={18} /></div>
      <p className="font-heading font-extrabold text-2xl">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

const ADM_DEVICE = {
  mobile: { icon: Smartphone, label: "Mobile" }, desktop: { icon: Monitor, label: "Ordinateur" },
  tablet: { icon: Tablet, label: "Tablette" }, tv: { icon: Tv, label: "Smart TV" },
};

function admFmtDuration(sec) {
  sec = sec || 0;
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  if (h) return `${h}h ${m}min`;
  if (m) return `${m}min`;
  return `${sec}s`;
}

function admCountryFlag(code) {
  if (!code || code.length !== 2) return "🌐";
  return code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

function AdmBar({ label, icon: Icon, count, total, testid }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      {Icon && <Icon size={16} className="text-primary shrink-0" />}
      <span className="text-sm w-32 shrink-0 truncate">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} /></div>
      <span className="text-xs font-semibold text-muted-foreground w-16 text-right">{count} · {pct}%</span>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [media, setMedia] = useState([]);
  const [users, setUsers] = useState([]);
  const [supports, setSupports] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [reports, setReports] = useState([]);
  const [logs, setLogs] = useState([]);
  const [langs, setLangs] = useState([]);
  const [cats, setCats] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [commission, setCommission] = useState("");
  const [pawapay, setPawapay] = useState({ enabled: false, token: "", environment: "sandbox", currency: "CDF", usd_rate: 2800, callback_public_key: "", callback_key_id: "" });
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [resend, setResend] = useState({ enabled: true, api_key: "", sender_email: "onboarding@resend.dev", api_key_set: false });
  const [stripe, setStripe] = useState({ enabled: false, secret_key: "", publishable_key: "", secret_key_set: false, env_fallback: false, webhook_secret: "", webhook_secret_set: false });
  const [publicUrl, setPublicUrl] = useState({ public_base_url: "", public_base_url_env: "" });
  const [newRef, setNewRef] = useState({ language: "", category: "", province: "" });
  const [analytics, setAnalytics] = useState(null);
  const [supportRank, setSupportRank] = useState(null);
  const [certs, setCerts] = useState([]);
  const [review, setReview] = useState(null);
  const [edit, setEdit] = useState({});
  const [streamRes, setStreamRes] = useState(null);
  const [streamChecking, setStreamChecking] = useState(false);
  const [savingMedia, setSavingMedia] = useState(false);
  const [certReview, setCertReview] = useState(null);
  const [certNote, setCertNote] = useState("");

  const loadAll = useCallback(async () => {
    const [s, m, u, sp, wd, rp, lg, ct, pv, st] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/media"), api.get("/admin/users"),
      api.get("/admin/supports"), api.get("/admin/withdrawals"), api.get("/admin/reports"),
      api.get("/languages"), api.get("/categories"), api.get("/provinces"), api.get("/admin/settings"),
    ]);
    setStats(s.data); setMedia(m.data); setUsers(u.data); setSupports(sp.data);
    setWithdrawals(wd.data); setReports(rp.data); setLangs(lg.data); setCats(ct.data);
    setProvinces(pv.data); setCommission(String(st.data.commission_percent));
    setPawapay({ enabled: false, token: "", environment: "sandbox", currency: "CDF", usd_rate: 2800, callback_key_id: "", ...(st.data.pawapay || {}), token: "", callback_public_key: "" });
    setResend({ enabled: true, sender_email: "onboarding@resend.dev", ...(st.data.resend || {}), api_key: "" });
    setStripe({ enabled: false, publishable_key: "", ...(st.data.stripe || {}), secret_key: "", webhook_secret: "" });
    setPublicUrl({ public_base_url: st.data.public_base_url || "", public_base_url_env: st.data.public_base_url_env || "" });
  }, []);

  useEffect(() => { loadAll(); api.get("/admin/logs").then((r) => setLogs(r.data)); api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {}); api.get("/admin/certifications").then((r) => setCerts(r.data)).catch(() => {}); api.get("/admin/rankings/supports?period=month").then((r) => setSupportRank(r.data)).catch(() => {}); api.get("/admin/webhook-logs").then((r) => setWebhookLogs(r.data)).catch(() => {}); }, [loadAll]);

  const openReview = (m) => {
    setReview(m); setStreamRes(null);
    setEdit({ name: m.name, description: m.description || "", province: m.province || "", city: m.city || "",
      website: m.website || "", languages: (m.languages || []).join(", "), categories: (m.categories || []).join(", "),
      hls_url: m.hls_url || "", audio_url: m.audio_url || "" });
  };
  const checkReviewStream = async () => {
    if (!review) return;
    setStreamChecking(true);
    try { const { data } = await api.post(`/admin/media/${review.id}/stream/check`); setStreamRes(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setStreamChecking(false); }
  };
  const saveMediaEdit = async () => {
    if (!review) return;
    setSavingMedia(true);
    try {
      const payload = { name: edit.name, description: edit.description, province: edit.province, city: edit.city,
        website: edit.website, languages: edit.languages.split(",").map((s) => s.trim()).filter(Boolean),
        categories: edit.categories.split(",").map((s) => s.trim()).filter(Boolean),
        hls_url: edit.hls_url, audio_url: edit.audio_url };
      await api.put(`/admin/media/${review.id}`, payload);
      toast.success("Détails de la chaîne enregistrés");
      const m = await api.get("/admin/media"); setMedia(m.data);
      setReview((r) => ({ ...r, ...payload }));
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingMedia(false); }
  };
  const reviewSetStatus = async (status) => {
    if (!review) return;
    await setStatus(review.id, status);
    setReview(null);
  };
  const decideCert = async (status) => {
    if (!certReview) return;
    try {
      await api.put(`/admin/media/${certReview.id}/certification`, { status, note: certNote });
      toast.success(status === "certified" ? "Chaîne certifiée 🏅" : "Certification refusée");
      setCertReview(null); setCertNote("");
      const [c, m] = await Promise.all([api.get("/admin/certifications"), api.get("/admin/media")]);
      setCerts(c.data); setMedia(m.data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!stats) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/media/${id}/status`, { status }); toast.success(`Statut: ${status}`); const m = await api.get("/admin/media"); setMedia(m.data); const s = await api.get("/admin/stats"); setStats(s.data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const assignManager = async (mediaId, managerId) => {
    try { await api.put(`/admin/media/${mediaId}/assign`, { manager_id: managerId }); toast.success("Gestionnaire attribué"); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleCertified = async (id, certified) => {
    try { await api.post(`/admin/media/${id}/toggle-certified`, { certified }); toast.success(certified ? "Chaîne certifiée 🏅" : "Certification retirée"); const m = await api.get("/admin/media"); setMedia(m.data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setRole = async (userId, role) => {
    try { await api.put(`/admin/users/${userId}/role`, { role }); toast.success("Rôle mis à jour"); const u = await api.get("/admin/users"); setUsers(u.data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setWithdrawalStatus = async (id, status) => {
    try { await api.put(`/admin/withdrawals/${id}/status`, { status }); toast.success(`Retrait: ${status}`); const wd = await api.get("/admin/withdrawals"); setWithdrawals(wd.data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const resolveReport = async (id, action) => {
    try { await api.put(`/admin/reports/${id}/resolve`, { action }); toast.success("Signalement traité"); const rp = await api.get("/admin/reports"); setReports(rp.data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveCommission = async () => {
    try { await api.put("/admin/settings", { commission_percent: parseFloat(commission) }); toast.success("Commission mise à jour"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const savePawapay = async () => {
    const payload = { enabled: pawapay.enabled, environment: pawapay.environment, currency: "USD", usd_rate: parseFloat(pawapay.usd_rate) || 2850, callback_key_id: pawapay.callback_key_id || "" };
    if (pawapay.token) payload.token = pawapay.token; // only send when changed
    if (pawapay.callback_public_key) payload.callback_public_key = pawapay.callback_public_key; // only send when changed
    try { await api.put("/admin/settings", { pawapay: payload }); toast.success("Configuration Mobile Money enregistrée"); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const refreshWebhookLogs = async () => {
    try { const { data } = await api.get("/admin/webhook-logs"); setWebhookLogs(data); toast.success("Journal actualisé"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveResend = async () => {
    const payload = { enabled: resend.enabled, sender_email: resend.sender_email };
    if (resend.api_key) payload.api_key = resend.api_key;
    try { await api.put("/admin/settings", { resend: payload }); toast.success("Configuration email enregistrée"); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveStripe = async () => {
    const payload = { enabled: stripe.enabled, publishable_key: stripe.publishable_key || "" };
    if (stripe.secret_key) payload.secret_key = stripe.secret_key;
    if (stripe.webhook_secret) payload.webhook_secret = stripe.webhook_secret;
    try { await api.put("/admin/settings", { stripe: payload }); toast.success("Configuration Stripe enregistrée"); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const savePublicUrl = async () => {
    try { await api.put("/admin/settings", { public_base_url: publicUrl.public_base_url || "" }); toast.success("Domaine public enregistré"); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addRef = async (kind) => {
    const name = newRef[kind];
    if (!name) return;
    try { await api.post(`/admin/${kind === "language" ? "languages" : kind === "category" ? "categories" : "provinces"}`, { name }); toast.success("Ajouté"); setNewRef((p) => ({ ...p, [kind]: "" })); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delRef = async (kind, id) => {
    try { await api.delete(`/admin/${kind}/${id}`); loadAll(); } catch (e) { toast.error(apiErr(e)); }
  };

  const managers = users.filter((u) => u.role === "manager" || u.role === "admin");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      <div className="flex items-center gap-2 mb-6"><Shield className="text-rdcred" /><h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Super Administration</h1></div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard icon={Tv} label="Télévisions" value={stats.total_tv} color="bg-rdcblue/10 text-rdcblue" />
        <StatCard icon={Radio} label="Radios" value={stats.total_radio} color="bg-rdcgold/10 text-rdcgold" />
        <StatCard icon={Clock} label="En attente" value={stats.pending_media} color="bg-amber-500/10 text-amber-600" />
        <StatCard icon={Users} label="Utilisateurs" value={stats.total_users} color="bg-primary/10 text-primary" />
        <StatCard icon={DollarSign} label="Soutiens ($)" value={stats.total_support_amount} color="bg-emerald-500/10 text-emerald-600" />
        <StatCard icon={Wallet} label="Commissions ($)" value={stats.total_commission} color="bg-rdcred/10 text-rdcred" />
      </div>

      <Tabs defaultValue="media">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="media" data-testid="adm-tab-media">Médias</TabsTrigger>
          <TabsTrigger value="certifications" data-testid="adm-tab-certifications">Certifications{certs.length > 0 ? ` (${certs.length})` : ""}</TabsTrigger>
          <TabsTrigger value="audience" data-testid="adm-tab-audience">Audience</TabsTrigger>
          <TabsTrigger value="users" data-testid="adm-tab-users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="supports" data-testid="adm-tab-supports">Soutiens</TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="adm-tab-withdrawals">Retraits</TabsTrigger>
          <TabsTrigger value="reports" data-testid="adm-tab-reports">Signalements</TabsTrigger>
          <TabsTrigger value="refs" data-testid="adm-tab-refs">Référentiels</TabsTrigger>
          <TabsTrigger value="settings" data-testid="adm-tab-settings">Paramètres</TabsTrigger>
          <TabsTrigger value="logs" data-testid="adm-tab-logs">Logs</TabsTrigger>
        </TabsList>

        {/* AUDIENCE / ANALYTICS */}
        <TabsContent value="audience">
          {!analytics ? (
            <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">Chargement des statistiques…</div>
          ) : (
            <div className="space-y-6">
              <LiveMap compact />

              <div className="rounded-2xl border border-border bg-card p-5" data-testid="admin-support-ranking">
                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                  <h3 className="font-heading font-bold flex items-center gap-2"><DollarSign size={18} className="text-emerald-600" /> Top chaînes soutenues — ce mois-ci</h3>
                  {supportRank && <span className="text-sm font-semibold text-muted-foreground">Total : {supportRank.total} · {supportRank.supporters} soutien(s)</span>}
                </div>
                {!supportRank || supportRank.items.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center" data-testid="admin-ranking-empty">Aucun soutien enregistré ce mois-ci.</p>
                ) : (
                  <ol className="space-y-2">
                    {supportRank.items.map((c, i) => (
                      <li key={c.id} className="flex items-center gap-3" data-testid={`support-rank-${i}`}>
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${i === 0 ? "bg-rdcgold text-black" : i === 1 ? "bg-slate-300 text-slate-800" : i === 2 ? "bg-amber-700/70 text-white" : "bg-secondary text-muted-foreground"}`}>{i + 1}</span>
                        <img src={c.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name)}&background=0B3D91&color=fff`} alt="" className="w-9 h-9 rounded-lg bg-white shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold truncate flex items-center gap-1">{c.name} {c.certified && <BadgeCheck size={13} className="text-rdcgold shrink-0" />}</p>
                          <p className="text-[11px] text-muted-foreground">{(c.type || "").toUpperCase()} · {c.province} · {c.count} soutien(s)</p>
                        </div>
                        <span className="text-sm font-extrabold text-emerald-600 shrink-0">{c.gross} {c.currency}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-2xl border border-border bg-card p-4">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2 bg-rdcblue/10 text-rdcblue"><Timer size={18} /></div>
                  <p className="font-heading font-extrabold text-2xl" data-testid="adm-total-watch-time">{admFmtDuration(analytics.total_watch_seconds)}</p>
                  <p className="text-xs text-muted-foreground">Temps total consommé</p>
                </div>
                <StatCard icon={BarChart3} label="Sessions de lecture" value={analytics.watch_sessions} color="bg-emerald-500/10 text-emerald-600" />
                <StatCard icon={Clock} label="Durée moyenne (s)" value={analytics.avg_session_seconds} color="bg-rdcgold/10 text-rdcgold" />
              </div>
              <p className="text-xs text-muted-foreground -mt-4">Statistiques globales (30 derniers jours) — appareils, pays d'origine et temps de consommation réel.</p>

              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><Smartphone size={18} className="text-primary" /> Par appareil</h3>
                {analytics.devices.length === 0 ? <p className="text-sm text-muted-foreground">Pas encore de données.</p> : (() => {
                  const total = analytics.devices.reduce((a, d) => a + d.count, 0);
                  return <div className="space-y-3">{analytics.devices.map((d) => { const meta = ADM_DEVICE[d.name] || { icon: Monitor, label: d.name }; return <AdmBar key={d.name} testid={`adm-device-${d.name}`} label={meta.label} icon={meta.icon} count={d.count} total={total} />; })}</div>;
                })()}
              </div>

              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><Globe size={18} className="text-rdcblue" /> Par pays</h3>
                {analytics.countries.length === 0 ? <p className="text-sm text-muted-foreground">Pas encore de données géographiques.</p> : (() => {
                  const total = analytics.countries.reduce((a, c) => a + c.count, 0);
                  return <div className="space-y-3">{analytics.countries.map((c) => <AdmBar key={c.code || c.name} testid={`adm-country-${c.code || "xx"}`} label={`${admCountryFlag(c.code)} ${c.name}`} count={c.count} total={total} />)}</div>;
                })()}
              </div>

              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><MapPin size={18} className="text-rdcred" /> Par province (RDC)</h3>
                {analytics.provinces.length === 0 ? <p className="text-sm text-muted-foreground">Pas encore de données.</p> : (() => {
                  const total = analytics.provinces.reduce((a, p) => a + p.count, 0);
                  return <div className="space-y-3">{analytics.provinces.map((p) => <AdmBar key={p.name} label={p.name} count={p.count} total={total} />)}</div>;
                })()}
              </div>
            </div>
          )}
        </TabsContent>

        {/* MEDIA */}
        <TabsContent value="media">
          <div className="space-y-2">
            {media.map((m) => (
              <div key={m.id} className="rounded-xl border border-border bg-card p-3 flex flex-wrap items-center gap-3" data-testid={`admin-media-row-${m.slug}`}>
                <img src={m.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=00A3E0&color=fff`} alt="" className="w-10 h-10 rounded-lg bg-white" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold flex items-center gap-1">{m.name} {m.certified && <BadgeCheck size={14} className="text-rdcgold" title="Chaîne certifiée" />}{m.verified && !m.certified && <BadgeCheck size={14} className="text-rdcblue" />}</p>
                  <p className="text-[11px] text-muted-foreground">{m.type.toUpperCase()} · {m.province}{(m.certification || {}).status === "pending" ? " · certification en attente" : ""}</p>
                </div>
                <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_COLORS[m.status]}`}>{m.status}</span>
                <Select value={m.manager_id || "none"} onValueChange={(v) => assignManager(m.id, v)}>
                  <SelectTrigger className="w-40 h-8 text-xs" data-testid={`assign-manager-${m.slug}`}><SelectValue placeholder="Gestionnaire" /></SelectTrigger>
                  <SelectContent>{managers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => openReview(m)} data-testid={`review-${m.slug}`} className="h-8 text-xs"><FolderOpen size={14} className="mr-1" /> Dossier</Button>
                  {m.certified
                    ? <Button size="sm" variant="outline" onClick={() => toggleCertified(m.id, false)} data-testid={`decertify-${m.slug}`} className="h-8 text-xs">Retirer certif.</Button>
                    : <Button size="sm" onClick={() => toggleCertified(m.id, true)} data-testid={`certify-${m.slug}`} className="h-8 text-xs bg-rdcgold text-amber-950 hover:bg-rdcgold/90"><BadgeCheck size={14} className="mr-1" /> Certifier</Button>}
                  {m.status !== "suspended" ? <Button size="sm" variant="destructive" onClick={() => setStatus(m.id, "suspended")} data-testid={`suspend-${m.slug}`} className="h-8 text-xs">Suspendre</Button>
                    : <Button size="sm" variant="outline" onClick={() => setStatus(m.id, "approved")} className="h-8 text-xs">Réactiver</Button>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* CERTIFICATIONS */}
        <TabsContent value="certifications">
          <div className="space-y-2">
            {certs.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="adm-certs-empty">Aucune demande de certification en attente.</p>}
            {certs.map((m) => (
              <div key={m.id} className="rounded-xl border border-border bg-card p-3 flex flex-wrap items-center gap-3" data-testid={`admin-cert-row-${m.slug}`}>
                <img src={m.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=F2A900&color=fff`} alt="" className="w-10 h-10 rounded-lg bg-white" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{m.name}</p>
                  <p className="text-[11px] text-muted-foreground">{m.type.toUpperCase()} · {m.province} · demandé le {new Date((m.certification || {}).requested_at || Date.now()).toLocaleDateString("fr-FR")}</p>
                </div>
                <Button size="sm" onClick={() => { setCertReview(m); setCertNote(""); }} data-testid={`cert-review-${m.slug}`} className="h-8 text-xs bg-rdcgold text-black hover:bg-rdcgold/90"><Award size={14} className="mr-1" /> Examiner</Button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users">
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            {users.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-border last:border-0">
                <span className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">{u.first_name?.[0]}</span>
                <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{u.name}</p><p className="text-[11px] text-muted-foreground truncate">{u.email}</p></div>
                <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                  <SelectTrigger className="w-36 h-8 text-xs" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="user">Utilisateur</SelectItem><SelectItem value="manager">Gestionnaire</SelectItem><SelectItem value="admin">Super Admin</SelectItem></SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* SUPPORTS */}
        <TabsContent value="supports">
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="hidden sm:grid grid-cols-6 gap-2 px-4 py-2 text-[11px] font-semibold text-muted-foreground uppercase bg-secondary">
              <span>Réf</span><span>Média</span><span>Utilisateur</span><span>Montant</span><span>Commission</span><span>Statut</span>
            </div>
            {supports.slice(0, 100).map((s) => (
              <div key={s.id} className="grid grid-cols-2 sm:grid-cols-6 gap-2 px-4 py-2.5 border-b border-border last:border-0 text-sm">
                <span className="font-mono text-[11px] truncate">{s.reference}</span>
                <span className="truncate">{s.media_name}</span>
                <span className="truncate text-muted-foreground">{s.user_name}</span>
                <span className="font-bold">${s.amount}</span>
                <span className="text-rdcred">${s.commission}</span>
                <span className={`text-[11px] font-medium ${s.status === "completed" ? "text-emerald-600" : "text-amber-600"}`}>{s.status}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* WITHDRAWALS */}
        <TabsContent value="withdrawals">
          <div className="space-y-2">
            {withdrawals.length === 0 ? <p className="text-muted-foreground text-center py-10">Aucune demande de retrait.</p> :
              withdrawals.map((x) => (
                <div key={x.id} className="rounded-xl border border-border bg-card p-3 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-0"><p className="text-sm font-semibold">{x.media_name} — ${x.amount}</p><p className="text-[11px] text-muted-foreground capitalize">{x.method} · {x.destination} · {new Date(x.created_at).toLocaleDateString("fr-FR")}</p></div>
                  <span className="rounded-md bg-secondary px-2 py-0.5 text-[11px] capitalize">{x.status}</span>
                  <Select value={x.status} onValueChange={(v) => setWithdrawalStatus(x.id, v)}>
                    <SelectTrigger className="w-36 h-8 text-xs" data-testid={`wd-status-${x.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="pending">En attente</SelectItem><SelectItem value="reviewing">En vérification</SelectItem><SelectItem value="approved">Approuvé</SelectItem><SelectItem value="paid">Payé</SelectItem><SelectItem value="rejected">Rejeté</SelectItem></SelectContent>
                  </Select>
                </div>
              ))}
          </div>
        </TabsContent>

        {/* REPORTS */}
        <TabsContent value="reports">
          <div className="space-y-2">
            {reports.length === 0 ? <p className="text-muted-foreground text-center py-10">Aucun signalement.</p> :
              reports.map((r) => (
                <div key={r.id} className="rounded-xl border border-border bg-card p-3 flex flex-wrap items-center gap-3">
                  <Flag size={16} className="text-rdcred shrink-0" />
                  <p className="flex-1 min-w-0 text-sm truncate">“{r.content}”</p>
                  <span className="rounded-md bg-secondary px-2 py-0.5 text-[11px] capitalize">{r.status}</span>
                  {r.status === "open" && <>
                    <Button size="sm" variant="destructive" onClick={() => resolveReport(r.id, "remove_comment")} className="h-8 text-xs" data-testid={`report-remove-${r.id}`}><XCircle size={13} className="mr-1" /> Supprimer</Button>
                    <Button size="sm" variant="outline" onClick={() => resolveReport(r.id, "dismiss")} className="h-8 text-xs" data-testid={`report-dismiss-${r.id}`}><CheckCircle2 size={13} className="mr-1" /> Ignorer</Button>
                  </>}
                </div>
              ))}
          </div>
        </TabsContent>

        {/* REFS */}
        <TabsContent value="refs">
          <div className="grid md:grid-cols-3 gap-4">
            {[["language", "Langues", langs, "languages"], ["category", "Catégories", cats, "categories"], ["province", "Provinces", provinces, "provinces"]].map(([kind, title, list, coll]) => (
              <div key={kind} className="rounded-2xl border border-border bg-card p-4">
                <h3 className="font-heading font-bold mb-3">{title}</h3>
                <div className="flex gap-2 mb-3">
                  <Input value={newRef[kind]} onChange={(e) => setNewRef((p) => ({ ...p, [kind]: e.target.value }))} placeholder="Nouveau…" className="h-9" data-testid={`ref-input-${kind}`} />
                  <Button size="sm" onClick={() => addRef(kind)} data-testid={`ref-add-${kind}`}><Plus size={15} /></Button>
                </div>
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  {list.map((it) => (
                    <div key={it.id} className="flex items-center justify-between text-sm px-2 py-1.5 rounded-lg hover:bg-secondary">
                      <span>{it.name}</span>
                      <button onClick={() => delRef(coll, it.id)} className="text-rdcred hover:opacity-70" data-testid={`ref-del-${it.id}`}><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* SETTINGS */}
        <TabsContent value="settings">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><SettingsIcon size={18} /> Commission de la plateforme</h3>
              <p className="text-sm text-muted-foreground mb-4">Pourcentage prélevé sur chaque soutien. Le reste est reversé au média.</p>
              <div className="flex items-center gap-2">
                <Input type="number" value={commission} onChange={(e) => setCommission(e.target.value)} className="w-28" data-testid="commission-input" />
                <span className="text-lg font-bold">%</span>
                <Button onClick={saveCommission} className="ml-auto" data-testid="commission-save">Enregistrer</Button>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><SettingsIcon size={18} /> Domaine public officiel</h3>
              <p className="text-sm text-muted-foreground mb-4">Utilisé pour les liens des QR codes des certificats (ex. https://rdcongo.tv). Laissez vide pour utiliser la variable d'environnement.</p>
              <div className="space-y-2">
                <Input placeholder="https://rdcongo.tv" value={publicUrl.public_base_url} onChange={(e) => setPublicUrl((p) => ({ ...p, public_base_url: e.target.value }))} data-testid="public-url-input" />
                <p className="text-[11px] text-muted-foreground">
                  {publicUrl.public_base_url ? `Actif : ${publicUrl.public_base_url}` : (publicUrl.public_base_url_env ? `Par défaut (env) : ${publicUrl.public_base_url_env}` : "Aucun domaine configuré : le domaine de la requête sera utilisé.")}
                </p>
                <Button onClick={savePublicUrl} className="w-full" data-testid="public-url-save">Enregistrer le domaine</Button>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-heading font-bold flex items-center gap-2"><Smartphone size={18} /> Mobile Money (PawaPay)</h3>
                <button onClick={() => setPawapay((p) => ({ ...p, enabled: !p.enabled }))} data-testid="pawapay-enabled"
                  className={`rounded-full px-3 py-1 text-xs font-bold ${pawapay.enabled ? "bg-emerald-500 text-white" : "bg-secondary"}`}>{pawapay.enabled ? "ACTIVÉ" : "DÉSACTIVÉ"}</button>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Ajoutez votre jeton API PawaPay pour activer les vrais paiements M-Pesa, Airtel & Orange Money. Tant que désactivé, les soutiens sont simulés. <b>Toutes les transactions sont effectuées en USD.</b></p>
              <div className="space-y-2">
                <Input type="password" placeholder={pawapay.token_set ? "•••• jeton configuré (laisser vide pour conserver)" : "Jeton API PawaPay (Bearer)"} value={pawapay.token} onChange={(e) => setPawapay((p) => ({ ...p, token: e.target.value }))} data-testid="pawapay-token" />
                <div className="grid grid-cols-2 gap-2 items-center">
                  <Select value={pawapay.environment} onValueChange={(v) => setPawapay((p) => ({ ...p, environment: v }))}>
                    <SelectTrigger data-testid="pawapay-env"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="sandbox">Sandbox</SelectItem><SelectItem value="production">Production</SelectItem></SelectContent>
                  </Select>
                  <span className="text-sm font-semibold text-center rounded-lg bg-secondary py-2" data-testid="pawapay-currency-badge">Devise : USD</span>
                </div>
                <Button onClick={savePawapay} className="w-full" data-testid="pawapay-save">Enregistrer la configuration</Button>
              </div>

              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm font-semibold flex items-center gap-1.5 mb-1"><ExternalLink size={15} className="text-rdcblue" /> URLs de callback (webhooks)</p>
                <p className="text-[11px] text-muted-foreground mb-3">Copiez ces adresses dans votre tableau de bord PawaPay (Dashboard › Callbacks) pour recevoir automatiquement les mises à jour de statut des paiements Mobile Money de la RDC.</p>
                {[["Paiement entrant (deposits)", `${API}/payments/pawapay/deposit-callback`, "cb-deposit"], ["Versement sortant (payouts)", `${API}/payments/pawapay/payout-callback`, "cb-payout"]].map(([label, url, tid]) => (
                  <div key={tid} className="mb-2">
                    <p className="text-xs font-medium mb-1">{label}</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-[11px] bg-secondary rounded-lg px-2.5 py-2 overflow-x-auto whitespace-nowrap" data-testid={`${tid}-url`}>{url}</code>
                      <Button size="sm" variant="outline" className="h-8 shrink-0" data-testid={`${tid}-copy`}
                        onClick={() => { navigator.clipboard.writeText(url); toast.success("URL copiée"); }}>Copier</Button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm font-semibold flex items-center gap-1.5 mb-1"><Shield size={15} className="text-emerald-600" /> Signature des callbacks (sécurité)</p>
                <p className="text-[11px] text-muted-foreground mb-2">Collez la clé publique PawaPay (format PEM) pour vérifier la signature des webhooks entrants. Récupérez-la via <code className="bg-secondary px-1 rounded">GET /v2/public-key/http</code> dans votre compte PawaPay. Laissez vide pour accepter les callbacks sans vérification.</p>
                <Textarea rows={4} placeholder={pawapay.callback_public_key_set ? "•••• clé configurée (laisser vide pour conserver)" : "-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----"} value={pawapay.callback_public_key} onChange={(e) => setPawapay((p) => ({ ...p, callback_public_key: e.target.value }))} data-testid="pawapay-callback-key" className="font-mono text-[11px]" />
                <Input placeholder="ID de clé (keyid) — optionnel" value={pawapay.callback_key_id || ""} onChange={(e) => setPawapay((p) => ({ ...p, callback_key_id: e.target.value }))} data-testid="pawapay-callback-keyid" className="mt-2" />
                <Button onClick={savePawapay} variant="outline" className="w-full mt-2" data-testid="pawapay-callback-save">Enregistrer la clé de signature</Button>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-heading font-bold flex items-center gap-2"><Mail size={18} /> Emails (Resend)</h3>
                <button onClick={() => setResend((p) => ({ ...p, enabled: !p.enabled }))} data-testid="resend-enabled"
                  className={`rounded-full px-3 py-1 text-xs font-bold ${resend.enabled ? "bg-emerald-500 text-white" : "bg-secondary"}`}>{resend.enabled ? "ACTIVÉ" : "DÉSACTIVÉ"}</button>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Envoi automatique d'un email au responsable lors de la validation ou du refus de son média. Ajoutez votre clé API Resend (re_…). Sans clé, aucun email n'est envoyé.</p>
              <div className="space-y-2">
                <Input type="password" placeholder={resend.api_key_set ? "•••• clé configurée (laisser vide pour conserver)" : "Clé API Resend (re_…)"} value={resend.api_key} onChange={(e) => setResend((p) => ({ ...p, api_key: e.target.value }))} data-testid="resend-key" />
                <Input placeholder="Adresse expéditeur (ex. no-reply@votredomaine.cd)" value={resend.sender_email} onChange={(e) => setResend((p) => ({ ...p, sender_email: e.target.value }))} data-testid="resend-sender" />
                <p className="text-[11px] text-muted-foreground">En mode test, utilisez <b>onboarding@resend.dev</b> (emails limités aux adresses vérifiées de votre compte Resend). Pour votre domaine, vérifiez-le d'abord sur resend.com.</p>
                <Button onClick={saveResend} className="w-full" data-testid="resend-save">Enregistrer la configuration</Button>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-heading font-bold flex items-center gap-2"><CreditCard size={18} /> Paiement par carte (Stripe)</h3>
                <button onClick={() => setStripe((p) => ({ ...p, enabled: !p.enabled }))} data-testid="stripe-enabled"
                  className={`rounded-full px-3 py-1 text-xs font-bold ${stripe.enabled ? "bg-emerald-500 text-white" : "bg-secondary"}`}>{stripe.enabled ? "ACTIVÉ" : "DÉSACTIVÉ"}</button>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Activez Stripe pour encaisser les soutiens par <b>carte bancaire</b> (en USD). Ajoutez votre clé secrète Stripe (<code className="bg-secondary px-1 rounded">sk_live_…</code> ou <code className="bg-secondary px-1 rounded">sk_test_…</code>). Sans clé, le paiement carte reste simulé.</p>
              {stripe.enabled && (
                stripe.secret_key_set ? (
                  <div className="flex items-start gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5 mb-3" data-testid="stripe-status-connected">
                    <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                    <p className="text-[13px] text-emerald-700 dark:text-emerald-400 leading-relaxed">Connecté à votre compte Stripe. Les paiements par carte utilisent votre vraie clé.</p>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-xl border border-rdcgold/50 bg-rdcgold/10 px-3 py-2.5 mb-3" data-testid="stripe-status-nokey">
                    <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-[13px] text-amber-700 dark:text-amber-400 leading-relaxed">
                      Stripe est activé mais <b>votre clé secrète n'est pas encore enregistrée</b>. Les paiements utilisent {stripe.env_fallback ? "une clé de test d'environnement" : "le mode simulé"}, pas votre vrai compte. Saisissez votre clé <code className="bg-black/5 dark:bg-white/10 px-1 rounded">sk_…</code> ci-dessous puis cliquez sur <b>Enregistrer</b>.
                    </p>
                  </div>
                )
              )}
              <div className="space-y-2">
                <Input type="password" placeholder={stripe.secret_key_set ? "•••• clé configurée (laisser vide pour conserver)" : "Clé secrète Stripe (sk_…)"} value={stripe.secret_key} onChange={(e) => setStripe((p) => ({ ...p, secret_key: e.target.value }))} data-testid="stripe-secret-key" />
                <Input placeholder="Clé publiable (pk_…) — optionnelle" value={stripe.publishable_key || ""} onChange={(e) => setStripe((p) => ({ ...p, publishable_key: e.target.value }))} data-testid="stripe-publishable-key" />
                <p className="text-[11px] text-muted-foreground">
                  {stripe.secret_key_set ? "Clé enregistrée." : (stripe.env_fallback ? "Aucune clé saisie : une clé de test d'environnement est utilisée par défaut." : "Aucune clé configurée.")} Carte de test : 4242 4242 4242 4242, date future, CVC quelconque.
                </p>

                <div className="rounded-xl border border-border bg-secondary/30 p-3 mt-1 space-y-2" data-testid="stripe-webhook-block">
                  <p className="text-xs font-heading font-bold flex items-center gap-1.5"><Signal size={14} className="text-primary" /> Vérification des webhooks (recommandé)</p>
                  <p className="text-[11px] text-muted-foreground">
                    Dans Stripe → Développeurs → Webhooks, créez un endpoint pointant vers l'URL ci-dessous, puis collez ici le <b>secret de signature</b> (<code className="bg-black/5 dark:bg-white/10 px-1 rounded">whsec_…</code>). Les confirmations de paiement seront alors vérifiées cryptographiquement (les webhooks falsifiés sont rejetés).
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 min-w-0 truncate rounded-lg bg-card border border-border px-2.5 py-1.5 text-[11px]" data-testid="stripe-webhook-url">{API}/webhook/stripe</code>
                    <button type="button" onClick={() => { navigator.clipboard?.writeText(`${API}/webhook/stripe`); toast.success("URL copiée"); }} data-testid="stripe-webhook-url-copy"
                      className="shrink-0 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium hover:border-primary/50">Copier</button>
                  </div>
                  <Input type="password" placeholder={stripe.webhook_secret_set ? "•••• secret configuré (laisser vide pour conserver)" : "Secret de signature du webhook (whsec_…)"} value={stripe.webhook_secret} onChange={(e) => setStripe((p) => ({ ...p, webhook_secret: e.target.value }))} data-testid="stripe-webhook-secret" />
                  <p className={`text-[11px] font-medium flex items-center gap-1 ${stripe.webhook_secret_set ? "text-emerald-600" : "text-amber-600"}`} data-testid="stripe-webhook-status">
                    {stripe.webhook_secret_set ? <><CheckCircle2 size={13} /> Vérification de signature active.</> : <><AlertTriangle size={13} /> Aucun secret : les webhooks ne sont pas vérifiés (les paiements restent confirmés par vérification directe auprès de Stripe).</>}
                  </p>
                </div>

                <Button onClick={saveStripe} className="w-full" data-testid="stripe-save">Enregistrer la configuration</Button>
              </div>
            </div>
          </div>

          {/* Webhook log */}
          <div className="rounded-2xl border border-border bg-card p-5 mt-4" data-testid="webhook-log-panel">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-bold flex items-center gap-2"><ScrollText size={18} /> Journal des webhooks PawaPay</h3>
              <Button size="sm" variant="outline" onClick={refreshWebhookLogs} data-testid="webhook-log-refresh">Actualiser</Button>
            </div>
            <p className="text-sm text-muted-foreground mb-3">Callbacks reçus (dépôts &amp; versements) — utile pour le débogage. Le statut de signature indique si le webhook était vérifié.</p>
            {webhookLogs.length === 0 ? (
              <p className="text-muted-foreground text-center py-8 text-sm" data-testid="webhook-log-empty">Aucun callback reçu pour l'instant.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b border-border">
                      <th className="py-2 pr-3">Reçu</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">ID paiement</th>
                      <th className="py-2 pr-3">Statut</th><th className="py-2 pr-3">Signature</th><th className="py-2 pr-3">Résultat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {webhookLogs.map((l) => (
                      <tr key={l.id} className="border-b border-border/60 last:border-0" data-testid={`webhook-log-row-${l.id}`}>
                        <td className="py-2 pr-3 whitespace-nowrap text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString("fr-FR")}</td>
                        <td className="py-2 pr-3">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${l.kind === "deposit" ? "bg-blue-500/15 text-blue-600" : "bg-amber-500/15 text-amber-600"}`}>{l.kind === "deposit" ? "Dépôt" : "Versement"}</span>
                        </td>
                        <td className="py-2 pr-3 font-mono text-[11px] max-w-[160px] truncate" title={l.payment_id}>{l.payment_id || "—"}</td>
                        <td className="py-2 pr-3 font-semibold text-xs">{l.status || "—"}</td>
                        <td className="py-2 pr-3">
                          {l.signature_reason === "valid" ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium"><CheckCircle2 size={13} /> Vérifiée</span>
                          ) : l.signature_reason === "unverified" ? (
                            <span className="inline-flex items-center gap-1 text-muted-foreground text-xs" title="Aucune clé de signature configurée">Non vérifiée</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-rdcred text-xs font-medium" title={l.signature_reason}><XCircle size={13} /> Invalide</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground max-w-[160px] truncate" title={l.result}>{l.result}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* LOGS */}
        <TabsContent value="logs">
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            {logs.length === 0 ? <p className="text-muted-foreground text-center py-10">Aucune opération enregistrée.</p> :
              logs.map((l) => (
                <div key={l.id} className="flex items-center gap-3 px-4 py-2 border-b border-border last:border-0 text-sm">
                  <ScrollText size={14} className="text-muted-foreground shrink-0" />
                  <span className="font-mono text-[11px] text-primary">{l.action}</span>
                  <span className="text-muted-foreground truncate flex-1">{l.actor_email}</span>
                  <span className="text-[11px] text-muted-foreground">{new Date(l.created_at).toLocaleString("fr-FR")}</span>
                </div>
              ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dossier / édition / validation modal */}
      <Dialog open={!!review} onOpenChange={(v) => { if (!v) setReview(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-review-modal">
          <DialogTitle className="sr-only">Dossier de la chaîne</DialogTitle>
          {review && (
            <div className="space-y-4">
              <div>
                <h3 className="font-heading font-bold text-lg flex items-center gap-2">{review.name} <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_COLORS[review.status]}`}>{review.status}</span></h3>
                <p className="text-xs text-muted-foreground">{review.type.toUpperCase()} · Responsable : {review.manager_name || "—"} · {review.email} · {review.phone}</p>
              </div>
              {review.banner && <img src={review.banner} alt="bannière" className="w-full aspect-[16/9] object-cover rounded-xl border border-border" />}
              <div className="flex items-center gap-3">
                <img src={review.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.name)}`} alt="logo" className="w-12 h-12 rounded-lg bg-white border border-border" />
                <p className="text-xs text-muted-foreground flex-1">{review.address || ""} {review.city || ""}</p>
              </div>

              <div className="rounded-xl border border-border p-3 bg-secondary/30">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-sm font-semibold flex items-center gap-1.5"><Signal size={15} /> Flux de diffusion</p>
                  <Button size="sm" variant="outline" onClick={checkReviewStream} disabled={streamChecking} data-testid="admin-test-stream">
                    {streamChecking ? <Loader2 size={14} className="animate-spin mr-1" /> : <Signal size={14} className="mr-1" />} Tester le flux
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground break-all">{review.hls_url || review.audio_url || "Aucune URL de flux"}</p>
                {streamRes && (
                  <p className={`text-xs font-semibold mt-2 ${streamRes.stream_status === "online" ? "text-emerald-600" : streamRes.stream_status === "offline" ? "text-rdcred" : "text-muted-foreground"}`} data-testid="admin-stream-result">
                    {streamRes.stream_status === "online" ? "✅ Flux accessible" : streamRes.stream_status === "offline" ? "⚠ Flux inaccessible" : "Aucune URL à tester"}
                  </p>
                )}
              </div>

              <details className="rounded-xl border border-border p-3" data-testid="admin-edit-details">
                <summary className="text-sm font-semibold cursor-pointer">Modifier les détails de la chaîne</summary>
                <p className="text-[11px] text-muted-foreground mt-1 mb-3">Vous modifiez les informations de la chaîne. Les accès du compte gestionnaire (email/mot de passe) ne sont pas modifiables ici.</p>
                <div className="space-y-2">
                  <Input placeholder="Nom" value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} data-testid="edit-name" />
                  <Textarea placeholder="Description" value={edit.description} onChange={(e) => setEdit((p) => ({ ...p, description: e.target.value }))} data-testid="edit-desc" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Province" value={edit.province} onChange={(e) => setEdit((p) => ({ ...p, province: e.target.value }))} data-testid="edit-province" />
                    <Input placeholder="Ville" value={edit.city} onChange={(e) => setEdit((p) => ({ ...p, city: e.target.value }))} data-testid="edit-city" />
                  </div>
                  <Input placeholder="Langues (séparées par virgule)" value={edit.languages} onChange={(e) => setEdit((p) => ({ ...p, languages: e.target.value }))} data-testid="edit-langs" />
                  <Input placeholder="Catégories (séparées par virgule)" value={edit.categories} onChange={(e) => setEdit((p) => ({ ...p, categories: e.target.value }))} data-testid="edit-cats" />
                  <Input placeholder="Site web" value={edit.website} onChange={(e) => setEdit((p) => ({ ...p, website: e.target.value }))} data-testid="edit-website" />
                  <Input placeholder="URL flux HLS (TV)" value={edit.hls_url} onChange={(e) => setEdit((p) => ({ ...p, hls_url: e.target.value }))} data-testid="edit-hls" />
                  <Input placeholder="URL flux audio (Radio)" value={edit.audio_url} onChange={(e) => setEdit((p) => ({ ...p, audio_url: e.target.value }))} data-testid="edit-audio" />
                  <Button onClick={saveMediaEdit} disabled={savingMedia} className="w-full" data-testid="admin-save-media">
                    {savingMedia ? <Loader2 size={15} className="animate-spin mr-1" /> : null} Enregistrer les détails
                  </Button>
                </div>
              </details>

              <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                {review.status !== "approved" && review.status !== "verified" && <Button onClick={() => reviewSetStatus("approved")} data-testid="review-approve" className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 size={15} className="mr-1" /> Valider la chaîne</Button>}
                {!review.verified && <Button onClick={() => reviewSetStatus("verified")} data-testid="review-verify" className="bg-rdcblue"><BadgeCheck size={15} className="mr-1" /> Valider + Vérifier</Button>}
                <Button variant="destructive" onClick={() => reviewSetStatus("rejected")} data-testid="review-reject"><XCircle size={15} className="mr-1" /> Rejeter</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Certification review modal */}
      <Dialog open={!!certReview} onOpenChange={(v) => { if (!v) setCertReview(null); }}>
        <DialogContent className="max-w-lg" data-testid="admin-cert-modal">
          <DialogTitle className="sr-only">Examen de certification</DialogTitle>
          {certReview && (
            <div className="space-y-4">
              <div>
                <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Award size={20} className="text-rdcgold" /> Certification — {certReview.name}</h3>
                <p className="text-xs text-muted-foreground">Examinez les documents fournis avant de certifier la chaîne.</p>
              </div>
              {(certReview.certification || {}).note && <p className="text-sm bg-secondary/40 rounded-lg p-3">« {(certReview.certification || {}).note} »</p>}
              <div className="space-y-2">
                {[["enterprise", "Document d'enregistrement de l'entreprise"], ["authorization", "Autorisation de diffusion"], ["id_card", "Carte d'identité du responsable"]].map(([k, label]) => {
                  const url = ((certReview.certification || {}).documents || {})[k];
                  return (
                    <a key={k} href={url || "#"} target="_blank" rel="noreferrer" onClick={(e) => { if (!url) e.preventDefault(); }}
                      data-testid={`cert-doc-${k}`}
                      className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${url ? "border-border hover:border-primary/50" : "border-dashed border-border opacity-50 cursor-not-allowed"}`}>
                      <FileText size={16} className="text-primary shrink-0" />
                      <span className="flex-1">{label}</span>
                      {url ? <ExternalLink size={14} className="text-muted-foreground" /> : <span className="text-[11px] text-muted-foreground">manquant</span>}
                    </a>
                  );
                })}
              </div>
              <Textarea placeholder="Note (optionnel, transmise au gestionnaire en cas de refus)" value={certNote} onChange={(e) => setCertNote(e.target.value)} data-testid="cert-note" />
              <div className="flex gap-2">
                <Button onClick={() => decideCert("certified")} data-testid="cert-approve" className="flex-1 bg-rdcgold text-black hover:bg-rdcgold/90"><BadgeCheck size={15} className="mr-1" /> Certifier</Button>
                <Button variant="destructive" onClick={() => decideCert("rejected")} data-testid="cert-reject" className="flex-1"><XCircle size={15} className="mr-1" /> Refuser</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
